import { useCallback, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { useProjectUploadStore } from '@/features/create-project/model/upload.store';
import { notificationService } from '@/lib/services/notification.service';
import { Dropzone } from '@/shared/ui/Dropzone/Dropzone';
import { FileListItem } from '@/shared/ui/FileListItem';
import { UploadFileIcon } from '@/shared/ui/Icons';
import {
  PROJECT_ACCEPTED_FORMATS,
  PROJECT_FILE_SIZE_LIMIT,
  buildProjectUploadShowcasedFormats,
} from './UploadDocumentsStep.constants';
import { useTranslation } from 'react-i18next';

/** Brand icon color — follows the monks-ui v4 primary token. */

export const UploadDocumentsStep = () => {
  const { t } = useTranslation('projects');

  const showcasedFormats = useMemo(() => buildProjectUploadShowcasedFormats(t), [t]);

  const files = useProjectUploadStore((s) => s.files);
  const addFiles = useProjectUploadStore((s) => s.addFiles);
  const removeFile = useProjectUploadStore((s) => s.removeFile);

  const handleDrop = useCallback(
    (droppedFiles: File[]) => {
      const validFiles = droppedFiles.filter((file) => file.size <= PROJECT_FILE_SIZE_LIMIT);
      const oversized = droppedFiles.filter((file) => file.size > PROJECT_FILE_SIZE_LIMIT);
      if (oversized.length > 0) {
        notificationService.showWarning(
          t('createProject.uploadDocumentsStep.filesExceededThe100MbSize', {
            fileCount: oversized.length,
          }),
          {
            description: oversized.map((f) => f.name).join(', '),
            persist: true,
          },
        );
      }

      const existingNames = new Set(files.map((f) => f.name));
      const newFiles: File[] = [];
      const duplicates: File[] = [];

      for (const f of validFiles) {
        if (existingNames.has(f.name)) {
          duplicates.push(f);
        } else {
          existingNames.add(f.name);
          newFiles.push(f);
        }
      }

      if (duplicates.length > 0) {
        notificationService.showWarning(
          t('createProject.uploadDocumentsStep.filesAlreadyAdded', {
            fileCount: duplicates.length,
          }),
          {
            description: duplicates.map((f) => f.name).join(', '),
            persist: true,
          },
        );
      }

      if (newFiles.length > 0) {
        addFiles(newFiles);
      }
    },
    [addFiles, files, t],
  );

  return (
    <div className="flex flex-col gap-12 w-full max-w-162.5">
      <div className="flex flex-col gap-4">
        <h2 className="text-center type-heading-md text-foreground">
          {t('createProject.uploadDocumentsStep.doYouWantToBuild')}
        </h2>
        <p className="text-center type-body-lg text-foreground-subtle">
          {t('createProject.uploadDocumentsStep.dropInADocumentBrief')}
        </p>
      </div>

      <div className="flex flex-col gap-6">
        <Dropzone
          onDrop={handleDrop}
          acceptedFormats={PROJECT_ACCEPTED_FORMATS}
          title={t('createProject.uploadDocumentsStep.dropDocumentsHere')}
          description={t('createProject.uploadDocumentsStep.maxSizeIs100Mb')}
          showcasedFormats={showcasedFormats}
          icon={<UploadFileIcon className="size-5 text-primary" />}
        />

        {files.length > 0 && (
          <div className="flex flex-col gap-2">
            <AnimatePresence>
              {files.map((file, index) => (
                <FileListItem
                  key={`${file.name}-${file.size}`}
                  fileName={file.name}
                  fileSize={file.size}
                  fileType={file.type}
                  onRemove={() => removeFile(index)}
                  delay={index * 0.05}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};
